import { useMemo } from 'react';

import { Country } from '@dreryk/address-common-api';

import { commonTranslationKeys, createTranslatedText, FormContextValues, getCurrentLocale } from '@dreryk/react-common';
import { Select, StyledMaxWidth, useSelectContext } from '@dreryk/react-components';

import { patientTranslationKeys } from '../../../../translations';
import { countryIsoCodeGetter, createCountryFilter } from '../../../common.utils';

type PartialFormBean = {
   passportCountry?: Country | null;
};

export type PassportCountryFieldProps = Pick<FormContextValues<PartialFormBean>, 'register'>;

const getCountryLabel = (item?: Country): string => item?.getName(getCurrentLocale()) ?? '';

const useCountries = (): Country[] => {
   return useMemo(() => {
      const locale = getCurrentLocale();
      return Country.searchAll(locale);
   }, []);
};

const CountriesList = (): JSX.Element => {
   const { searchText } = useSelectContext();
   const countries = useCountries();
   const filteredCountries = countries.filter(createCountryFilter(searchText));
   if (!filteredCountries.length) {
      return <Select.NoItems testId={'noCountries'} />;
   }
   return (
      <Select.List items={countries.filter(createCountryFilter(searchText))} keyExtractor={countryIsoCodeGetter}>
         {(country: Country) => (
            <Select.Item<Country>
               item={country}
               testId={country.getIsoCode()}
               label={<Select.Highlight node={createTranslatedText(getCountryLabel(country))} />}
            />
         )}
      </Select.List>
   );
};

export const PassportCountryField = (props: PassportCountryFieldProps): JSX.Element => {
   return (
      <StyledMaxWidth maxWidth={308}>
         <Select<Country>
            testId={'passportCountry'}
            triggerLabelExtractor={getCountryLabel}
            controller={props.register('passportCountry', { required: true })}
            label={patientTranslationKeys.passportCountry}
            placeholder={commonTranslationKeys.choose}
            maxHeight={190}
            searchInputEnabled
            defaultValue={null}
         >
            <CountriesList />
         </Select>
      </StyledMaxWidth>
   );
};
